import React, { useState, useEffect, useCallback } from 'react'
import { Keyboard } from 'react-native'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import PatientActions from 'APP/Redux/PatientRedux'

import Config from 'APP/Config'
import { Images } from 'APP/Themes'
import I18n from 'APP/I18n'
import { useDevTools } from 'APP/Lib/DevTools'
import { getShouldProvideProvince } from 'APP/Lib/ProfileHelpers'
import { validateIsValidDate, validateIsAdult } from 'APP/Lib/DateHelpers'
import { KeyLabelArrays } from 'APP/Lib/ProfileDataFormat'
import { getValidationRulesForTextField } from 'APP/Lib/FormHelpers'

import DialogueBranding from 'APP/Components/DialogueBranding'
import LogoHeader from 'APP/Components/LogoHeader'
import ControllerDatePicker from 'APP/Components/ControllerFields/ControllerDatePicker'
import ControllerInput from 'APP/Components/ControllerFields/ControllerInput'
import ControllerPicker from 'APP/Components/ControllerFields/ControllerPicker'
import ControllerRadioGroup from 'APP/Components/ControllerFields/ControllerRadioGroup'
import Pronouns from 'APP/Components/Pronouns'
import { ResponsiveView, ResponsiveScrollView } from 'APP/Converse/Layout'
import Button from 'APP/Converse/Button'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const FormContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const OnboardingProfileScreen = ({
  profile,
  signupProfileData,
  busy,
  patientUpdateBasicProfile,
  setIsOnboarding,
}) => {
  const [focusedField, setFocusedField] = useState(null)
  const { setToolFunction, setToolName } = useDevTools()

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    clearErrors,
    setValue,
    formState: { isDirty, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: {
      firstName: profile.givenName || signupProfileData.firstName,
      lastName: profile.familyName || signupProfileData.lastName,
      preferredName: profile.preferredName,
      preferredNameRadio: profile.preferredName ? 'yes' : null,
      pronouns: profile.pronouns,
      pronounsRadio: profile.pronouns ? 'yes' : null,
      province: profile.adminAreaISOCode ? profile.adminAreaISOCode.split('-')[1] : null,
      phoneNumber: profile.phoneNumber,
      preferredLanguage:
        profile.preferred_language ||
        (KeyLabelArrays.LANGUAGES.length === 1 && KeyLabelArrays.LANGUAGES[0].key),
      dateOfBirth: profile.dateOfBirth || signupProfileData.dateOfBirth,
    },
  })

  const watchPreferredNameRadio = watch('preferredNameRadio')
  const watchPronounRadio = watch('pronounsRadio')

  const showCommunicationPreference = KeyLabelArrays.LANGUAGES.length > 1

  const onSubmit = useCallback(
    (formData) => {
      if (formData.preferredNameRadio === 'no') {
        formData.preferredName = null // Ensure we don't send a preferred name if the user selects no
      }
      if (formData.pronounsRadio === 'no') {
        formData.pronouns = null // Ensure we don't send pronouns if the user selects no
      }
      patientUpdateBasicProfile(formData)
      setIsOnboarding(true)
    },
    [patientUpdateBasicProfile, setIsOnboarding]
  )

  const fillWithData = useCallback(() => {
    const userName = profile.email.split('@')[0]
    setValue('firstName', userName.split?.('.')?.[0])
    setValue('lastName', userName.split?.('.')?.[1])
    setValue('dateOfBirth', '1990-01-01')
    setValue('province', 'QC')
    setValue('phoneNumber', '5555555555')
    setValue('preferredLanguage', 'en')
    setValue('preferredNameRadio', 'no')
    setValue('pronounsRadio', 'no')
    handleSubmit(onSubmit)()
  }, [profile.email, setValue, handleSubmit, onSubmit])

  useEffect(() => {
    setToolName('Skip')
    setToolFunction(() => fillWithData)
    return () => {
      setToolName(null)
      setToolFunction(null)
    }
  }, [setToolFunction, setToolName, fillWithData])

  const renderFirstName = () => {
    return (
      <ControllerInput
        name="firstName"
        rules={getValidationRulesForTextField(I18n.t('ProfileScreen.error.invalidGivenName'), {
          noUrls: true,
          safeChars: true,
          required: true,
        })}
        testID="GIVEN_NAME_INPUT"
        control={control}
        label={I18n.t('ProfileScreen.givenName')}
        infotip={I18n.t('ProfileScreen.hint.nameHint')}
        noBottomMargin={true}
      />
    )
  }

  const renderLastName = () => {
    return (
      <ControllerInput
        name="lastName"
        rules={getValidationRulesForTextField(I18n.t('ProfileScreen.error.invalidFamilyName'), {
          noUrls: true,
          safeChars: true,
          required: true,
        })}
        testID="FAMILY_NAME_INPUT"
        control={control}
        label={I18n.t('ProfileScreen.familyName')}
        infotip={I18n.t('ProfileScreen.hint.nameHint')}
        noBottomMargin={true}
      />
    )
  }

  const renderDateOfBirth = () => {
    return (
      <ControllerDatePicker
        scrollViewRef={null}
        control={control}
        name="dateOfBirth"
        testID="DATE_OF_BIRTH_PICKER"
        rules={{
          required: I18n.t('ProfileScreen.error.invalidBdayRequiredField'),
          validate: {
            isValid: (value) =>
              validateIsValidDate(value) || I18n.t('ProfileScreen.error.invalidDate'),
            isAdult: (value) => validateIsAdult(value) || I18n.t('ProfileScreen.error.invalidBday'),
          },
        }}
        isFocused={focusedField === 'dateOfBirth'}
        label={I18n.t('ProfileScreen.bdayWeb')}
        onFocus={() => setFocusedField('dateOfBirth')}
        value={getValues().dateOfBirth}
        onSelect={() => {
          setFocusedField(null)
          trigger('dateOfBirth')
        }}
        setValue={(_, value) => setValue('dateOfBirth', value)}
        clearErrors={clearErrors}
        noBottomMargin={true}
      />
    )
  }

  const renderProvinces = () => {
    const shouldProvideProvince = getShouldProvideProvince()

    if (!shouldProvideProvince) return

    const sortedProvinces = KeyLabelArrays.PROVINCES.sort((a, b) => a.label.localeCompare(b.label))

    return (
      <ControllerPicker
        name="province"
        rules={{
          required: I18n.t('ProfileScreen.error.invalidJurisdictionRequiredField'),
        }}
        control={control}
        label={I18n.t('UpdateProfileScreen.province')}
        placeholder={I18n.t('ProfileScreen.residesIn')}
        items={sortedProvinces}
        testID="UPDATE_PROFILE_SCREEN_PROVINCE_FIELD"
        isFocused={focusedField === 'jurisdiction'}
        analyticsName={'province'}
        noBottomMargin={true}
      />
    )
  }

  const renderPhoneNumber = () => {
    return (
      <ControllerInput
        name="phoneNumber"
        rules={getValidationRulesForTextField(I18n.t('ProfileScreen.error.invalidNumber'), {
          noUrls: true,
          required: true,
        })}
        control={control}
        label={I18n.t('ProfileScreen.number')}
        infotip={I18n.t('ProfileScreen.hint.numberHint')}
        testID="UPDATE_PROFILE_SCREEN_PHONE_NUMBER_FIELD"
        keyboardType="phone-pad"
        noBottomMargin={true}
      />
    )
  }

  const renderPreferredLanguage = () => {
    return (
      <ControllerPicker
        name="preferredLanguage"
        rules={{
          required: I18n.t('ProfileScreen.error.invalidLanguage'),
        }}
        control={control}
        label={I18n.t('ProfileScreen.language')}
        placeholder={I18n.t('UpdateProfileScreen.communicationPref')}
        items={KeyLabelArrays.LANGUAGES}
        testID="UPDATE_PROFILE_LANG_INPUT"
        isFocused={focusedField === 'language'}
        analyticsName="Language"
        noBottomMargin={true}
      />
    )
  }

  const renderPreferredName = () => {
    const showPreferredName = watchPreferredNameRadio === 'yes'
    const firstName = getValues().firstName
    const radioLabel = firstName
      ? `${I18n.t('ProfileScreen.preferredNameRadio')}: ${firstName}?`
      : `${I18n.t('ProfileScreen.preferredNameRadio')}?`
    return (
      <>
        <ControllerRadioGroup
          name={'preferredNameRadio'}
          control={control}
          rules={{
            required: true,
            value: I18n.t('ProfileScreen.error.invalidPreferredName'),
          }}
          label={radioLabel}
          options={[
            {
              label: I18n.t('ProfileScreen.yes'),
              value: 'yes',
            },
            {
              label: I18n.t('ProfileScreen.no'),
              value: 'no',
            },
          ]}
        />
        <ControllerInput
          name="preferredName"
          control={control}
          rules={{
            required: showPreferredName
              ? I18n.t('ProfileScreen.error.invalidPreferredName')
              : false,
          }}
          label={I18n.t('ProfileScreen.preferredName')}
          infotip={I18n.t('ProfileScreen.hint.preferredNameHint')}
          testID="UPDATE_PROFILE_SCREEN_PREFERRED_NAME_FIELD"
          hide={!showPreferredName}
          noBottomMargin={true}
        />
      </>
    )
  }

  const renderPronouns = () => {
    return (
      <Pronouns
        id="onboarding-profile-pronouns"
        name="pronouns"
        shouldShowPronounRadio={true}
        watchPronounRadio={watchPronounRadio}
        onFocus={() => setFocusedField('pronouns')}
        isFocused={focusedField === 'pronouns'}
        control={control}
        label={I18n.t('ProfileScreen.pronouns')}
        noBottomMargin={true}
      />
    )
  }

  return (
    <ScreenContainer>
      <ResponsiveScrollView>
        <ContentContainer>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('OnboardingProfileScreen.title')}
            subtitle={I18n.t('OnboardingProfileScreen.subtitle')}
          />
          <FormContainer>
            {renderFirstName()}
            {renderLastName()}
            {renderDateOfBirth()}
            {renderProvinces()}
            {renderPhoneNumber()}
            {showCommunicationPreference && renderPreferredLanguage()}
            {renderPreferredName()}
            {renderPronouns()}
          </FormContainer>
          <Button
            variant="primary"
            widthVariant="full"
            title={I18n.t('OnboardingProfileScreen.next')}
            analyticsName="Next"
            onPress={() => {
              Keyboard.dismiss()
              setFocusedField(null)
              handleSubmit(onSubmit)()
            }}
            isLoading={busy}
            disabled={!isDirty || !isValid}
          />
          <DialogueBranding />
        </ContentContainer>
      </ResponsiveScrollView>
    </ScreenContainer>
  )
}

const mapStateToProps = (state, props) => {
  return {
    ...props.route?.params,
    busy: state.patient.requestRunning,
    profile: state.patient.profile || {},
    signupProfileData: state.login.signupProfileData || {},
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    patientUpdateBasicProfile: ({
      firstName,
      preferredName,
      pronouns,
      lastName,
      dateOfBirth,
      province,
      phoneNumber,
      preferredLanguage,
    }) =>
      dispatch(
        PatientActions.patientProfileBasicUpdateRequest({
          givenName: firstName.trim(),
          preferredName: preferredName?.trim(),
          pronouns: pronouns?.trim(),
          familyName: lastName.trim(),
          dateOfBirth: dateOfBirth,
          adminAreaISOCode: Config.REGION === 'CA' ? `CA-${province}` : null,
          phoneNumber: phoneNumber.replace(/\D+/g, ''),
          preferred_language: preferredLanguage,
        })
      ),
    setIsOnboarding: (value) => dispatch(PatientActions.setIsOnboarding(value)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OnboardingProfileScreen)
